'use client';

import React, { useEffect, useState } from 'react';
import { getABTestVariant, trackABTestConversion, isABTestingEnabled } from '@/lib/ab-testing';

interface ABTestWrapperProps {
  testId: string;
  variants: {
    [key: string]: React.ReactNode;
  };
  fallback?: React.ReactNode;
}

export function ABTestWrapper({ testId, variants, fallback }: ABTestWrapperProps) {
  const [variant, setVariant] = useState<string>('control');
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    if (!isABTestingEnabled()) {
      setVariant('control');
      setIsLoaded(true);
      return;
    }

    const assignedVariant = getABTestVariant(testId);
    setVariant(assignedVariant);
    setIsLoaded(true);
  }, [testId]);

  // Track conversion when user interacts with CTA
  const handleConversion = (conversionType: string = 'click') => {
    if (isABTestingEnabled()) {
      trackABTestConversion(testId, conversionType);
    }
  };

  if (!isLoaded) {
    return fallback || variants.control || null;
  }

  const VariantComponent = variants[variant] || variants.control || fallback;
  
  // Clone element and add conversion tracking if it's a button or link
  if (React.isValidElement(VariantComponent)) {
    return React.cloneElement(VariantComponent as React.ReactElement, {
      onClick: (e: React.MouseEvent) => {
        handleConversion('click');
        if (VariantComponent.props.onClick) {
          VariantComponent.props.onClick(e);
        }
      }
    });
  }

  return VariantComponent as React.ReactElement;
}

// Hook for A/B testing in components
export function useABTest(testId: string) {
  const [variant, setVariant] = useState<string>('control');
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    if (!isABTestingEnabled()) {
      setVariant('control');
      setIsLoaded(true);
      return;
    }

    const assignedVariant = getABTestVariant(testId);
    setVariant(assignedVariant);
    setIsLoaded(true);
  }, [testId]);

  const trackConversion = (conversionType: string = 'conversion') => {
    if (isABTestingEnabled()) {
      trackABTestConversion(testId, conversionType);
    }
  };

  return {
    variant,
    isLoaded,
    trackConversion,
    isVariant: (variantId: string) => variant === variantId
  };
}
